import { addDays, addHours, isAfter, isBefore, set, startOfDay, subMinutes } from "date-fns";

import { MAX_AFTER_SHIFT_END_THRESHOLD_HOURS } from "./constants";

export interface InferDateFromTimeParams {
  /**
   * The time input from the time picker (hour and minute only)
   */
  timeInput: { hour: number; minute: number };
  /**
   * The shift start time (in local timezone)
   */
  shiftStart: Date;
  /**
   * The shift end time (in local timezone)
   */
  shiftEnd: Date;
  /**
   * Early buffer in minutes before shift start (default: 30)
   */
  earlyBufferMinutes?: number;
}

export type InferDateResult =
  | { success: true; date: Date }
  | { success: false; error: "OUTSIDE_VALID_WINDOW" | "AMBIGUOUS_DATE" };

const DEFAULT_EARLY_BUFFER_MINUTES = 30;

/**
 * Infers the correct calendar date for a time input based on the shift window.
 *
 * For shift windows under 24 hours, each time (HH:MM) can only validly fall on
 * ONE calendar date within the window. This function determines which date that is.
 *
 * The shift window is defined as:
 * - Start: shiftStart - earlyBufferMinutes (default 30 minutes)
 * - End: shiftEnd + 6 hours (MAX_AFTER_SHIFT_END_THRESHOLD_HOURS)
 *
 * @example
 * // Overnight shift 11pm-7am (Jan 15-16)
 * // Window: Jan 15 10:30pm → Jan 16 1:00pm
 * inferDateFromTime({
 *   timeInput: { hour: 1, minute: 0 }, // 1:00 AM
 *   shiftStart: new Date("2024-01-15T23:00:00"),
 *   shiftEnd: new Date("2024-01-16T07:00:00"),
 * });
 * // Returns: { success: true, date: Jan 16 1:00am }
 */
export function inferDateFromTime(params: InferDateFromTimeParams): InferDateResult {
  const {
    timeInput,
    shiftStart,
    shiftEnd,
    earlyBufferMinutes = DEFAULT_EARLY_BUFFER_MINUTES,
  } = params;

  const { hour, minute } = timeInput;

  const windowStart = subMinutes(shiftStart, earlyBufferMinutes);
  const windowEnd = addHours(shiftEnd, MAX_AFTER_SHIFT_END_THRESHOLD_HOURS);

  // Use the window start day as the base, not the shift start day.
  // This handles cases where the early buffer extends to the previous day
  // (e.g., midnight shift with 30-min early buffer starts at 11:30pm the day before)
  const baseDay = startOfDay(windowStart);

  // Try placing time on window start day (Day 1)
  const onDay1 = set(baseDay, {
    hours: hour,
    minutes: minute,
    seconds: 0,
    milliseconds: 0,
  });

  // Try placing time on the next day (Day 2)
  const onDay2 = addDays(onDay1, 1);

  const inWindowDay1 = isInWindow(onDay1, windowStart, windowEnd);
  const inWindowDay2 = isInWindow(onDay2, windowStart, windowEnd);

  if (inWindowDay1 && !inWindowDay2) {
    return { success: true, date: onDay1 };
  }

  if (inWindowDay2 && !inWindowDay1) {
    return { success: true, date: onDay2 };
  }

  if (!inWindowDay1 && !inWindowDay2) {
    return { success: false, error: "OUTSIDE_VALID_WINDOW" };
  }

  // Both valid - this shouldn't happen for windows < 24 hours
  return { success: false, error: "AMBIGUOUS_DATE" };
}

function isInWindow(time: Date, windowStart: Date, windowEnd: Date): boolean {
  return !isBefore(time, windowStart) && !isAfter(time, windowEnd);
}
